import {GirafeHTMLElement} from '@geogirafe/lib-geoportal/base';
import {IGirafePanel} from '@geogirafe/lib-geoportal/core';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import CircleStyle from 'ol/style/Circle';
import type OlMap from 'ol/Map';

class SitnWastewaterflowComponent extends GirafeHTMLElement implements IGirafePanel {
  override templateUrl = './template.html';
  styleUrls = ['./style.css'];

  isPanelVisible = false;
  panelTitle = 'wastewaterflow-panel';
  panelTogglePath = 'extendedState.interface.wastewaterflowPanelVisible';

  streamtype = 'downstream';
  precision = 0.01;
  limit = 100;
  loading = false;
  showWarning = false;
  distanceError = false;

  get effectiveLimit() {
    return Math.min(this.limit, 500);
  }

  private readonly vectorSource = new VectorSource();
  private readonly vectorLayer: VectorLayer<VectorSource>;
  private olMap: OlMap | null = null;
  private clickHandler: ((e: {coordinate: number[]}) => void) | null = null;

  constructor() {
    super('sitn-wastewaterflow');
    this.vectorLayer = new VectorLayer({
      source: this.vectorSource,
      style: new Style({
        stroke: new Stroke({color: 'rgba(0, 210, 90, 0.6)', width: 4}),
        image: new CircleStyle({
          radius: 6,
          fill: new Fill({color: 'rgba(0, 210, 90, 0.4)'}),
          stroke: new Stroke({color: 'rgba(0, 210, 90, 0.6)', width: 1}),
        }),
      }),
      zIndex: 100,
    });
  }

  override connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  override render() {
    super.render();
    if (this.isPanelVisible) {
      this.show();
    } else {
      this.hide();
    }
  }

  togglePanel(visible: boolean) {
    if (this.isPanelVisible === visible) return;
    this.isPanelVisible = visible;
    if (visible) {
      this.openPanel();
    } else {
      this.closePanel();
    }
    this.render();
  }

  private openPanel() {
    const map = this.context.mapManager.getMap();
    this.olMap = map;
    this.vectorLayer.setMap(map);
    this.registerInteractionListener('map.select', true);
    const handler = (e: {coordinate: number[]}) => {
      if (this.canExecute('map.select')) {
        void this.onMapClick(e.coordinate[0], e.coordinate[1]);
      }
    };
    this.clickHandler = handler;
    map.on('singleclick', handler as (e: object) => void);
    const target = map.getTargetElement();
    if (target instanceof HTMLElement) {
      target.style.cursor = 'crosshair';
    }
  }

  private closePanel() {
    const map = this.olMap;
    if (map) {
      const handler = this.clickHandler;
      if (handler) {
        map.un('singleclick', handler as (e: object) => void);
        this.clickHandler = null;
      }
      const target = map.getTargetElement();
      if (target instanceof HTMLElement) {
        target.style.cursor = '';
      }
      this.olMap = null;
    }
    this.vectorLayer.setMap(null);
    this.vectorSource.clear();
    this.unregisterInteractionListeners();
    this.loading = false;
    this.showWarning = false;
    this.distanceError = false;
  }

  private async onMapClick(lon: number, lat: number) {
    this.vectorSource.clear();
    this.loading = true;
    this.showWarning = false;
    this.distanceError = false;
    this.refreshRender();
    try {
      const extConf = this.context.configManager.Config.extendedConfig;
      const wwfConf = extConf?.['wastewaterflow'] as {serviceUrl?: string} | undefined;
      const serviceUrl = wwfConf?.serviceUrl ?? '/wastewaterflow';
      const params = new URLSearchParams({
        lon: String(lon),
        lat: String(lat),
        layers: 'cseu1_conduites',
        streamtype: this.streamtype,
        precision: String(this.precision),
        limit: String(Math.min(this.limit, 500)),
      });
      const response = await fetch(`${serviceUrl}?${params}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = (await response.json()) as {distance_error?: boolean};
      if (data.distance_error) {
        this.distanceError = true;
      } else {
        const features = new GeoJSON().readFeatures(data as object);
        this.vectorSource.addFeatures(features);
        if (features.length > 0 && features[0].getProperties()['limit'] === true) {
          this.showWarning = true;
        }
      }
    } catch (err) {
      console.error('Wastewaterflow service error:', err);
      this.state.infobox.elements.push({
        id: `wwf-${Math.random().toString(36).slice(2)}`,
        text: "Erreur lors du calcul d'écoulement. Merci de contacter le SITN (sitn@ne.ch).",
        type: 'error',
        duration: 5000,
      });
    } finally {
      this.loading = false;
      this.refreshRender();
    }
  }
}

export default SitnWastewaterflowComponent;
